import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { styled } from "@mui/material/styles";
import { SaveDialogProps } from "designSystem/components/dialog/SaveDialog";
import { Box, Button, DataGrid, DataGridCardProps, FlexColumn, FlexRowCenter, FormControlLabel, GalleryPlaceholder, Grid, GridColumn, GridRow, IconButton, InfoIconButton, Message, OverflowText, Radio, RadioGroup, SaveDialog, Switch, Text } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import RulePane from "csi/middleware/attributeRule/RulePane";
import { RuleStoreContext } from "csi/middleware/attributeRule/RuleStore";
import { useStore } from "csi/state/hooks";
import { C8Node } from "csi/typings";
import { action } from "mobx";
import { observer } from "mobx-react-lite";
import React, { ChangeEvent, FunctionComponent, useEffect, useState } from "react";
import PPProductCard, { COLUMNS_BY_WIDTH, PPProductCardSkeleton } from "./NewProductPresentation/PPProductCard";
import { usePPWizardStore } from "./NewProductPresentation/PPWizardStore";
import PredictViolationProductCard from "./NewProductPresentation/PredictViolationProductCard";
import { ProductGalleryStoreContext } from "./NewProductPresentation/ProductGalleryStore";
import PPAttributeSetting from "./PPAttributeSetting";
import { usePPProductLayoutStore } from "./PPProductLayoutStore";

interface PPEditRuleDialogProps extends Omit<SaveDialogProps, "onSave"> {

}
const PREFIX = "GridColumn";

const classes = {
	info: `${PREFIX}info`,
	transparentCards: `${PREFIX}transparentCards`,
};

const StyledGridColumn = styled(GridColumn)(({ theme }) => ({
	[`& .${classes.info}`]: {
		marginLeft: 4,
	},
	[`& .${classes.transparentCards}`]: {
		opacity: 0.25,
	},
}));

const AddFromRulesLabel = observer(() => {
	const {
		primaryType,
		resultCountLimit,
		resultCountOverLimit,
	} = usePPWizardStore() || {};

	const { info } = classes;
	const resParams = [useLocale().type(primaryType, true), resultCountLimit];

	return (
		<FlexRowCenter height={20}>
			<Text res="PPAddFromRules" resParams={resParams} />
			{resultCountOverLimit && (
				<InfoIconButton
					className={info}
					res="PPAddFromRulesLimited"
					resParams={resParams}
				/>
			)}
		</FlexRowCenter>
	);
});

const RadioButtons = observer(() => {
	const {
		disableAddStyles,
		primaryType,
		setAddStyles,
		shouldAddStyles,
	} = usePPWizardStore() || {};

	const resParams = [useLocale().type(primaryType, true)];

	const onRadio = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target as HTMLInputElement;
		setAddStyles(value === "true");
	};

	return (
		<RadioGroup
			onChange={onRadio}
			value={String(shouldAddStyles)}
		>
			<FormControlLabel
				control={<Radio />}
				disabled={disableAddStyles}
				label={<AddFromRulesLabel />}
				labelPlacement="end"
				value="true"
			/>
			<FormControlLabel
				control={<Radio />}
				labelPlacement="end"
				res="PPDontAddFromRules"
				resParams={resParams}
				value="false"
			/>
		</RadioGroup>
	);
});

const PPGalleryMessage = observer(() => {
	const {
		primaryType,
		productsAreLoading,
		resultCount,
	} = usePPWizardStore() || {};
	const typeName = useLocale().type(primaryType, resultCount != 1);

	return productsAreLoading ?
		<Text res="FindingMatches" variant="subtitle1" />
		: (
			<Text
				res="PPAvailableProductToAdd"
				resParams={[resultCount, typeName]}
				variant="subtitle1"
			/>
		);
});

const PPGalleryPlaceholder = () => {
	return (
		<GalleryPlaceholder titleProps={{ text: "" }} />
	);
};

const PPProductCardForEditRule: FunctionComponent<DataGridCardProps<C8Node>> = (props) => <PPProductCard hideUsedInSection {...props} />;

const PPGallery = observer(() => {
	const {
		productStore,
		resultCount,
		shouldAddStyles,
	} = usePPWizardStore() || {};

	const { transparentCards } = classes;

	return (
		<DataGrid
			CardComponent={PPProductCardForEditRule}
			PlaceholderComponent={PPGalleryPlaceholder}
			SkeletonComponent={PPProductCardSkeleton}
			className={shouldAddStyles || !resultCount ? null : transparentCards}
			columns={4}
			spacing={3}
			store={productStore}
		/>
	);
});

const PPRulePane = observer(() => {
	const {
		attributeSettingStore,
		ruleStore,
	} = usePPWizardStore() || {};

	return (
		<RuleStoreContext.Provider value={ruleStore}>
			<RulePane
				SettingComponent={PPAttributeSetting}
				settingComponentStore={attributeSettingStore}
			/>
		</RuleStoreContext.Provider>
	);
});

const VariationMessage = observer(() => {
	const { goToViolationView, futureViolationUrls, primaryType } = usePPWizardStore();
	const loc = useLocale();

	const numberText = loc.numberOfType(futureViolationUrls.size, primaryType);
	const description = loc.resource("PresentationInvalidProductsMessageSubtitle", [loc.type(primaryType, true)]);

	return (
		<>
			{futureViolationUrls?.size > 0 && (
				<Message
					description={description}
					titleRes="PresentationInvalidProductsMessageForEditRuleDialog"
					titleResParams={[numberText]}
					variant="banner"
				>{{
						buttons: (
							<Grid container>
								<Grid item>
									<Button
										design="secondary"
										onClick={goToViolationView}
										res="ViewAll"
									/>
								</Grid>
							</Grid>
						),
					}}
				</Message>
			)}
		</>
	);
});

const PPViolationGallery = observer(() => {
	const { futureGalleryViolationStore, futureViolationDataStore } = usePPWizardStore();

	return (
		<ProductGalleryStoreContext.Provider value={futureGalleryViolationStore}>
			<DataGrid
				CardComponent={PredictViolationProductCard}
				SkeletonComponent={PPProductCardSkeleton}
				columnsByWidth={COLUMNS_BY_WIDTH}
				spacing={3}
				store={futureViolationDataStore}
			/>
		</ProductGalleryStoreContext.Provider>
	);
});

const ViolationView = observer(() => {
	const { backToEditRuleView, futureViolationDataStore, futureViolationUrls, primaryType } = usePPWizardStore();
	const { localStore: { productPresentation } } = useStore();
	const { showAttributesInDialog } = productPresentation;
	const loc = useLocale();

	const toggleShowAttributes = action(() => {
		productPresentation.showAttributesInDialog = !showAttributesInDialog;
	});

	const numberText = loc.numberOfType(futureViolationUrls.size, primaryType);
	const description = loc.resource("PresentationInvalidProductsMessageSubtitle", [loc.type(primaryType, true)]);

	return (
		<FlexColumn width={1}>
			<FlexRowCenter>
				<IconButton
					aria-label="back"
					onClick={backToEditRuleView}
					size="large"
					style={{ marginRight: 4 }}
				>
					<ArrowBackIcon />
				</IconButton>
				<Box pl={1}>
					<Text res="Back" />
				</Box>
			</FlexRowCenter>
			<FlexRowCenter py={3}>
				<GridColumn grow spacing={1}>
					<GridRow>
						<OverflowText
							bold
							res="PresentationInvalidProductsMessageForEditRuleDialog"
							resParams={[numberText]}
						/>
					</GridRow>
					<GridRow>
						<OverflowText text={description} />
					</GridRow>
				</GridColumn>
				<Box>
					<FormControlLabel
						control={(
							<Switch
								checked={showAttributesInDialog}
								onChange={() => toggleShowAttributes()}
							/>
						)}
						labelPlacement="start"
						res="DetailView"
					/>
				</Box>
			</FlexRowCenter>
			{futureViolationDataStore && (
				<FlexRowCenter overflow="auto">
					<FlexColumn flexGrow={1} style={{ height: "100%" }}>
						<PPViolationGallery />
					</FlexColumn>
				</FlexRowCenter>
			)}
		</FlexColumn>
	);
});

const PPEditRuleDialog: FunctionComponent<PPEditRuleDialogProps> = observer((props) => {
	const { open, ...otherProps } = props;
	const [_open, setOpen] = useState<boolean>(open);
	const {
		activeView,
		futureViolationUrls,
		galleryStore,
		updateRule,
	} = usePPWizardStore() || {};
	const { createDataStore } = usePPProductLayoutStore() || {};

	/** handle save logic */
	const onSave = async () => {
		try {
			await updateRule();

			// update the pp layout store to get the latest changes
			createDataStore();
			return true;
		} catch (ex) {
			console.error(ex);
			return false;
		}
	};

	useEffect(() => {
		setOpen(open);
	}, [open]);

	const EditRuleView = (
		<StyledGridColumn
			flexWrap="nowrap"
			growChild={2}
			spacing={5}
		>
			<PPRulePane />
			{futureViolationUrls?.size > 0 && (
				<Box height={87}>
					<VariationMessage />
				</Box>
			)}
			{galleryStore && (
				<GridColumn spacing={5}>
					<RadioButtons />
					<GridColumn spacing={2}>
						<PPGalleryMessage />
						<ProductGalleryStoreContext.Provider value={galleryStore}>
							<PPGallery />
						</ProductGalleryStoreContext.Provider>
					</GridColumn>
				</GridColumn>
			)}
		</StyledGridColumn>
	);

	const main = activeView === "editRuleView" ? EditRuleView : <ViolationView />;

	return (
		<SaveDialog
			fullHeight
			onSave={onSave}
			open={_open}
			titleRes="PPEditRule"
			width={1110}
			{...otherProps}
		>
			{{ main }}
		</SaveDialog>
	);
});

export default PPEditRuleDialog;